import {useEffect, useRef, useState} from "react";

import {createClientId} from "../utils/create-client-id";

export interface PendingAttachment {
  file: File;
  id: string;
  mimeType?: string;
  name: string;
  size: number;
  src?: string;
}

type UploadedAttachmentSource =
  | {
      mimeType: string;
      type: "data";
      value: string;
    }
  | {
      mimeType?: string;
      type: "url";
      value: string;
    };

interface UploadedAttachment {
  contentType: string;
  id: string;
  name: string;
  size: number;
  source: UploadedAttachmentSource;
}

type AttachmentContentType = "audio" | "document" | "image" | "video";

type UserMessageContentPart =
  | {text: string; type: "text"}
  | {
      metadata: {
        attachmentId: string;
        filename: string;
        size: number;
      };
      source: UploadedAttachmentSource;
      type: AttachmentContentType;
    };

function createAttachmentId(file: File): string {
  return `${file.name}-${file.lastModified}-${createClientId("attachment")}`;
}

function revokeAttachmentUrl(attachment: PendingAttachment) {
  if (attachment.src?.startsWith("blob:")) URL.revokeObjectURL(attachment.src);
}

function revokeAttachmentUrls(attachments: readonly PendingAttachment[]) {
  attachments.forEach(revokeAttachmentUrl);
}

function getAttachmentContentType(mimeType: string): AttachmentContentType {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("video/")) return "video";

  return "document";
}

function fileToDataSource(file: File): Promise<UploadedAttachmentSource> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(reader.error ?? new Error(`Attachment read failed: ${file.name}`));
    };

    reader.onload = () => {
      const result = reader.result;

      if (typeof result !== "string") {
        reject(new Error(`Unexpected attachment payload type: ${file.name}`));

        return;
      }

      const commaIndex = result.indexOf(",");
      const value = commaIndex >= 0 ? result.slice(commaIndex + 1) : result;

      resolve({
        mimeType: file.type || "application/octet-stream",
        type: "data",
        value,
      });
    };

    reader.readAsDataURL(file);
  });
}

export function buildUserMessageContent(
  text: string,
  uploadedAttachments: UploadedAttachment[],
): string | UserMessageContentPart[] {
  const trimmedText = text.trim();

  if (uploadedAttachments.length === 0) return trimmedText;

  const contentParts: UserMessageContentPart[] = trimmedText
    ? [{text: trimmedText, type: "text"}]
    : [];

  uploadedAttachments.forEach((attachment) => {
    contentParts.push({
      metadata: {
        attachmentId: attachment.id,
        filename: attachment.name,
        size: attachment.size,
      },
      source: attachment.source,
      type: getAttachmentContentType(attachment.contentType),
    });
  });

  return contentParts;
}

async function uploadAttachments(attachments: PendingAttachment[]): Promise<UploadedAttachment[]> {
  if (attachments.length === 0) return [];

  return Promise.all(
    attachments.map(async (attachment) => ({
      contentType: attachment.mimeType || "application/octet-stream",
      id: attachment.id,
      name: attachment.name,
      size: attachment.size,
      source: await fileToDataSource(attachment.file),
    })),
  );
}

export function useAssistantAttachments() {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const attachmentsRef = useRef<PendingAttachment[]>([]);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  useEffect(() => {
    return () => revokeAttachmentUrls(attachmentsRef.current);
  }, []);

  const handleFilesSelected = (files: File[]) => {
    setAttachments((current) => [
      ...current,
      ...files.map((file) => ({
        file,
        id: createAttachmentId(file),
        mimeType: file.type,
        name: file.name,
        size: file.size,
        src: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      })),
    ]);
  };

  const removeAttachment = (id: string) => {
    setAttachments((current) => {
      const removed = current.find((attachment) => attachment.id === id);

      if (removed) revokeAttachmentUrl(removed);

      return current.filter((attachment) => attachment.id !== id);
    });
  };

  const clearAttachments = () => {
    revokeAttachmentUrls(attachments);
    setAttachments([]);
  };

  return {
    attachments,
    clearAttachments,
    handleFilesSelected,
    removeAttachment,
    uploadAttachments: () => uploadAttachments(attachments),
  };
}
